import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors, loginWithGoogle, register, login } from '../actions/userAction';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import countries from 'i18n-iso-countries';
import './Signup.css';

const PENDING_KEY = 'dmyor_vision_pending';

const Signup = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isLogin = location.pathname === '/login';

  const { error, isAuthenticated } = useSelector((state) => state.user);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);

  const validateEmail = (val) => {
    if (!val) return 'Email is required';
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!re.test(val)) return 'Enter a valid email address';
    return '';
  };

  const validatePassword = (val) => {
    if (!val) return 'Password is required';
    if (isLogin) return '';
    if (val.length < 8) return 'At least 8 characters';
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/;
    if (!re.test(val)) return 'Include upper, lower, number, symbol';
    return '';
  };

  const validateForm = () => {
    const errs = {};
    const e = validateEmail(email);
    if (e) errs.email = e;
    const p = validatePassword(password);
    if (p) errs.password = p;
    if (!isLogin) {
      if (!name.trim()) errs.name = 'Name is required';
      if (!selectedCountry) errs.country = 'Pick a country';
    }
    return errs;
  };

  useEffect(() => {
    countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
    const list = Object.entries(countries.getNames('en'))
      .map(([code, n]) => ({ code, name: n }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setCountryList(list);
  }, []);

  // Clear fields/errors when switching between login and signup
  useEffect(() => {
    setErrors({});
    setPassword('');
  }, [isLogin]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateForm();
    setErrors(errs);
    if (Object.keys(errs).length !== 0) return;
    setBtnLoading(true);
    try {
      if (isLogin) {
        await dispatch(login(email.trim(), password));
      } else {
        await dispatch(register({ name: name.trim(), email: email.trim(), password, country: selectedCountry }));
      }
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Something went wrong');
    } finally {
      setBtnLoading(false);
    }
  };

  const redirect = new URLSearchParams(location.search).get('next');

  const googleAuth = () => {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed?.text || parsed?.category) {
        dispatch(loginWithGoogle('/dashboard/start'));
        return;
      }
    } catch (_) {}
    dispatch(loginWithGoogle(redirect || undefined));
  };

  useEffect(() => {
    if (error) dispatch(clearErrors());
    if (!isAuthenticated) return;
    // Pending vision flow takes priority
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed?.text || parsed?.category) {
        navigate('/dashboard/start', { replace: true });
        return;
      }
    } catch (_) {}
    navigate(redirect || '/dashboard', { replace: true });
  }, [error, isAuthenticated, dispatch, navigate, redirect]);

  const toggleHref = (isLogin ? '/signup' : '/login') + location.search;

  return (
    <div className="su-page">
      <Helmet>
        <title>{isLogin ? 'Welcome Back — dmyor' : 'Join dmyor — Put Your First Record Down'}</title>
        <meta name="description" content={isLogin
          ? 'Log in to dmyor and continue planning, documenting, and building toward the future you wrote for yourself.'
          : 'Create your account and start planning your future on dmyor. Write your first record, track your progress, and document the journey as you go.'} />
        <meta property="og:title" content={isLogin ? 'Welcome Back — dmyor' : 'Join dmyor — Put Your First Record Down'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dmyor.com${isLogin ? '/login' : '/signup'}`} />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* LEFT — story panel */}
      <aside className="su-side">
        <div className="su-side-bg" aria-hidden="true">
          <div className="su-orb su-orb-a" />
          <div className="su-orb su-orb-b" />
          <div className="su-grid" />
        </div>

        <div className="su-side-inner">
          <Link to="/" className="su-brand">dmyor</Link>

          <div className="su-tagline-wrap">
            <h2 className="su-tagline">
              Never let your <span className="su-hero-accent">dreams</span> die.
            </h2>
          </div>

          <span className="su-side-foot-empty" aria-hidden="true" />
        </div>
      </aside>

      {/* RIGHT — form */}
      <main className="su-main">
        <div className="su-card">
          {/* Login / Signup toggle */}
          <div className="su-toggle" role="tablist">
            <Link
              to={`/login${location.search}`}
              className={`su-toggle-btn${isLogin ? ' su-toggle-btn--active' : ''}`}
              role="tab"
              aria-selected={isLogin}
            >
              Log in
            </Link>
            <Link
              to={`/signup${location.search}`}
              className={`su-toggle-btn${!isLogin ? ' su-toggle-btn--active' : ''}`}
              role="tab"
              aria-selected={!isLogin}
            >
              Sign up
            </Link>
          </div>

          <header className="su-card-head">
            <h1 className="su-title">{isLogin ? 'Welcome back' : 'Create your account'}</h1>
          </header>

          <button
            type="button"
            className="su-google"
            onClick={googleAuth}
          >
            <svg className="su-google-svg" viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="su-divider"><span>{isLogin ? 'or log in with email' : 'or sign up with email'}</span></div>

          <form onSubmit={submit} noValidate>
            {!isLogin && (
              <div className={`su-field ${errors.name ? 'has-error' : ''}`}>
                <label htmlFor="su-name">Full name</label>
                <input
                  id="su-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
                {errors.name && <span className="su-error">{errors.name}</span>}
              </div>
            )}

            <div className={`su-field ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="su-email">Email</label>
              <input
                id="su-email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setErrors((p) => ({ ...p, email: validateEmail(email) }))}
                autoComplete="email"
              />
              {errors.email && <span className="su-error">{errors.email}</span>}
            </div>

            {!isLogin && (
              <div className={`su-field ${errors.country ? 'has-error' : ''}`}>
                <label htmlFor="su-country">Country</label>
                <select
                  id="su-country"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  <option value="">Select a country</option>
                  {countryList.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                {errors.country && <span className="su-error">{errors.country}</span>}
              </div>
            )}

            <div className={`su-field ${errors.password ? 'has-error' : ''}`}>
              <label htmlFor="su-password">Password</label>
              <div className="su-input-wrap">
                <input
                  id="su-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isLogin ? 'Your password' : 'Create a strong password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setErrors((p) => ({ ...p, password: validatePassword(password) }))}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  className="su-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {isLogin ? (
                <Link to="/password-reset" className="su-forgot">Forgot password?</Link>
              ) : (
                <span className="su-hint">8+ chars, with upper, lower, number, symbol.</span>
              )}
              {errors.password && <span className="su-error">{errors.password}</span>}
            </div>

            <button type="submit" className="su-submit" disabled={btnLoading}>
              {btnLoading
                ? (isLogin ? 'Logging in…' : 'Creating account…')
                : (isLogin ? 'Log in' : 'Create account')}
            </button>
          </form>

          {!isLogin && (
            <p className="su-terms">
              By continuing you agree to dmyor's <Link to="/terms">Terms</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          )}

          <p className="su-login-link">
            {isLogin ? "New to dmyor? " : 'Already have an account? '}
            <Link to={toggleHref}>{isLogin ? 'Create an account' : 'Sign in'}</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Signup;
